import { useForm, router } from '@inertiajs/react'
import { NotaryPortalLayout } from '@/components/layout/NotaryPortalLayout'
import { FormSection } from '@/components/backoffice/FormSection'
import { SectionHeader } from '@/components/backoffice/SectionHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Trash2 } from 'lucide-react'
import { NotaryTimeOff } from '@/types'

type Props = {
  time_offs: NotaryTimeOff[]
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function handleDelete(id: string) {
  if (!confirm('Remove this time-off entry?')) return
  router.delete(`/notary_portal/time_offs/${id}`)
}

export default function TimeOffsIndex({ time_offs }: Props) {
  const { data, setData, post, processing, errors, reset } = useForm({
    start_date: '',
    end_date: '',
    reason: '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/notary_portal/time_offs', {
      data: { time_off: data },
      onSuccess: () => reset(),
    })
  }

  return (
    <NotaryPortalLayout title="Time Off">
      <div className="space-y-8">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Time Off</h1>
          <p className="text-sm text-muted-foreground">Manage your scheduled time off from appointments</p>
        </div>

        {/* Current and upcoming time-offs */}
        <div>
          <SectionHeader title="Scheduled Time Off" count={time_offs.length} />
          {time_offs.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">No time off scheduled.</p>
            </div>
          ) : (
            <div className="mt-3 space-y-1">
              {time_offs.map((timeOff) => (
                <div key={timeOff.id} className="flex items-center justify-between py-2.5 border-b border-border/40 last:border-0 group">
                  <div>
                    <p className="text-sm font-medium">
                      {formatDate(timeOff.start_date)} &ndash; {formatDate(timeOff.end_date)}
                    </p>
                    {timeOff.reason && (
                      <p className="text-[12px] text-muted-foreground mt-0.5">{timeOff.reason}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(timeOff.id)}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add time-off form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormSection title="Add Time Off">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Start Date</label>
                <Input
                  className="h-8 text-sm"
                  type="date"
                  value={data.start_date}
                  onChange={(e) => setData('start_date', e.target.value)}
                />
                {errors.start_date && <p className="text-[12px] text-destructive">{errors.start_date}</p>}
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">End Date</label>
                <Input
                  className="h-8 text-sm"
                  type="date"
                  value={data.end_date}
                  onChange={(e) => setData('end_date', e.target.value)}
                />
                {errors.end_date && <p className="text-[12px] text-destructive">{errors.end_date}</p>}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Reason (optional)</label>
              <Textarea
                className="text-sm min-h-15"
                value={data.reason}
                onChange={(e) => setData('reason', e.target.value)}
                placeholder="Vacation, personal day, etc."
              />
              {errors.reason && <p className="text-[12px] text-destructive">{errors.reason}</p>}
            </div>
          </FormSection>

          <Button type="submit" size="sm" disabled={processing} className="h-8 text-[13px]">
            Add Time Off
          </Button>
        </form>
      </div>
    </NotaryPortalLayout>
  )
}
